import React, { useEffect, useMemo, useRef, useState } from 'react';
import './guessing.css';
import AudioPlayer from '../AudioPlayer';
import gameshowTheme from '../../sound/guessing/gameshow.mp3';
import guessAgainTheme from '../../sound/guessing/guessagain.mp3';
import lastGuessTheme from '../../sound/guessing/lastguess.mp3';

const PlayerScreen = ({
  socket,
  sessionId,
  playerName,
  isMusicMuted = false,
}) => {
  const [state, setState] = useState({
    phase: 'lobby',
    mode: 'gameshow',
    settings: {},
    board: [],
    scoreboard: [],
    currentQuestion: null,
    chosenHostId: null,
    currentSelectorId: null,
    finalGuess: { started: false },
    spiral: null,
  });
  const [isBuzzEnabled, setBuzzEnabled] = useState(false);
  const [choices, setChoices] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [hostVoteSubmitted, setHostVoteSubmitted] = useState(false);
  const [wager, setWager] = useState(0);
  const [finalAnswer, setFinalAnswer] = useState('');
  const [finalGuessSubmitted, setFinalGuessSubmitted] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [hostAnswer, setHostAnswer] = useState({
    answer: '',
    questionId: null,
    category: '',
    value: 0,
  });
  const [buzzersOpen, setBuzzersOpen] = useState(false);
  const clueAudioRef = useRef(null);
  const audioRevisionRef = useRef(0);

  useEffect(() => {
    if (!socket) return undefined;

    const updateState = (incoming) => setState((prev) => ({ ...prev, ...incoming }));

    socket.on('guessingState', updateState);
    socket.on('guessingBuzzOpen', () => setBuzzEnabled(true));
    socket.on('guessingBuzzed', ({ playerId } = {}) => {
      setBuzzEnabled(false);
      setBuzzersOpen(false);
      if (playerId && playerId !== socket.id) {
        setChoices([]);
      }
    });
    socket.on('guessingAnswerChoices', ({ choices: incoming }) => setChoices(incoming));
    socket.on('guessingHostAnswer', (payload) => setHostAnswer(payload || { answer: '', questionId: null, category: '', value: 0 }));

    socket.emit('guessingSyncState', { sessionId });

    return () => {
      socket.off('guessingState', updateState);
      socket.off('guessingBuzzOpen');
      socket.off('guessingBuzzed');
      socket.off('guessingAnswerChoices');
      socket.off('guessingHostAnswer');
    };
  }, [socket, sessionId]);

  const me = useMemo(
    () => state.scoreboard?.find((entry) => entry.name === playerName),
    [state.scoreboard, playerName]
  );

  const leaderboardRows = useMemo(
    () => [...(state.scoreboard || [])].sort((a, b) => b.points - a.points),
    [state.scoreboard]
  );

  useEffect(() => {
    if (state.phase === 'lobby') {
      setShowLeaderboard(false);
      setHostVoteSubmitted(false);
    }
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== 'host-vote') {
      setHostVoteSubmitted(false);
    }
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== 'final-guess') {
      setFinalGuessSubmitted(false);
      setWager(0);
      setFinalAnswer('');
    }
  }, [state.phase]);

  useEffect(() => {
    setBuzzersOpen(false);
    setChoices([]);
  }, [state.currentQuestion?.id]);

  useEffect(() => {
    const audio = clueAudioRef.current;
    if (!audio) return;

    const media = state.currentQuestion?.media;
    const source = state.currentQuestion?.audio || '';
    const revision = Number(media?.audioRevision || 0);
    const audioState = media?.audioState || 'stopped';

    if (!source) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audioRevisionRef.current = 0;
      return;
    }

    if (audio.getAttribute('src') !== source) {
      audio.setAttribute('src', source);
      audio.load();
    }

    if (revision !== audioRevisionRef.current) {
      audio.currentTime = 0;
      audioRevisionRef.current = revision;
    }

    if (audioState === 'playing') {
      audio.play().catch(() => {});
      return;
    }

    if (audioState === 'paused') {
      audio.pause();
      return;
    }

    audio.pause();
    audio.currentTime = 0;
  }, [
    state.currentQuestion?.audio,
    state.currentQuestion?.media?.audioState,
    state.currentQuestion?.media?.audioRevision,
    state.currentQuestion?.id,
  ]);

  const buzz = () => {
    socket.emit('guessingBuzz', { sessionId });
    setBuzzEnabled(false);
  };

  const requestHint = () => socket.emit('guessingRequestHint', { sessionId });

  const submitChoice = (answer) => {
    socket.emit('guessingSubmitAnswer', { sessionId, answer });
    setChoices([]);
  };

  const voteForHost = () => {
    if (!selectedCandidate) return;
    socket.emit('guessingVoteHost', { sessionId, candidateId: selectedCandidate });
    setHostVoteSubmitted(true);
  };

  const submitFinal = () => {
    socket.emit('guessingSubmitWager', { sessionId, wager: Number(wager) || 0 });
    socket.emit('guessingSubmitFinalAnswer', { sessionId, answer: finalAnswer });
    setFinalGuessSubmitted(true);
  };

  const selectQuestion = (cellId) => {
    socket.emit('guessingSelectQuestion', { sessionId, cellId });
  };

  const openBuzz = () => {
    socket.emit('guessingAllowBuzz', { sessionId });
    setBuzzersOpen(true);
  };

  const closeBuzzers = () => {
    socket.emit('guessingCloseBuzzers', { sessionId });
    setBuzzersOpen(false);
  };

  const returnToBoard = () => {
    socket.emit('guessingReturnToBoard', { sessionId });
    setBuzzersOpen(false);
  };

  const awardScore = (correct) => {
    if (!state.currentQuestion?.buzzerId) return;
    socket.emit('guessingHostScore', {
      sessionId,
      playerId: state.currentQuestion.buzzerId,
      correct,
    });
  };

  const scoreFinal = (playerId, correct) => {
    socket.emit('guessingHostFinalScore', { sessionId, playerId, correct });
  };

  const handleMediaToggle = () => {
  if (isImageVisible) {
    mediaControl('hide-image');
  } else {
    mediaControl('show-image');
  }
  setIsImageVisible(!isImageVisible);
};

  const mediaControl = (action) => {
    socket.emit('guessingHostMediaControl', { sessionId, action });
  };

  const iAmHost = me?.id && state.chosenHostId === me.id;
  const iAmSelector = me?.id && state.currentSelectorId === me.id;
  const iAmBuzzer = me?.id && state.currentQuestion?.buzzerId === me.id;
  const iAmSpiralTurn = me?.id && state.spiral?.currentTurnId === me.id;
  const myPlayerId = me?.id || socket?.id;
  const finalGuessLocked = finalGuessSubmitted || Boolean(state.finalGuess?.answers?.[myPlayerId]?.answer);
  const isQuestionLocked = state.currentQuestion?.status === 'locked';
  const hostModeEnabled = Boolean(state.settings?.hostSelectionEnabled);
  const isGameShowMode = state.mode === 'gameshow';
  const inGuessingRound = state.phase !== 'lobby' && state.phase !== 'final-guess' && state.phase !== 'ended';
  const hasActiveQuestionAudio = Boolean(state.currentQuestion?.audio);
  const isRoundOneMusic = isGameShowMode && Number(state.round || 1) === 1 && inGuessingRound && !hasActiveQuestionAudio;
  const isGuessAgainMusic = isGameShowMode && Number(state.round || 1) === 2 && inGuessingRound && !hasActiveQuestionAudio;
  const isLastGuessMusic = isGameShowMode && state.phase === 'final-guess';
  const canSelectBoardQuestion = state.mode === 'gameshow' && state.phase === 'board' && (
    (hostModeEnabled && iAmHost) || (!hostModeEnabled && iAmSelector)
  );
  const leaderboardRowsFiltered = hostModeEnabled && state.chosenHostId
    ? leaderboardRows.filter((entry) => entry.id !== state.chosenHostId)
    : leaderboardRows;

  const buzzStatusLabel = () => {
    if (state.phase !== 'question') return 'Waiting';
    if (state.currentQuestion?.status === 'hints-exhausted') return 'Buzz locked';
    if (state.currentQuestion?.buzzerId) return iAmBuzzer ? 'You buzzed' : 'Buzz locked';
    if (state.settings?.hostSelectionEnabled && !state.currentQuestion?.buzzOpen) {
      return iAmHost ? 'Host opens buzzing' : 'Waiting for host to open buzzing';
    }
    if (state.settings?.hostSelectionEnabled && state.currentQuestion?.buzzOpen) return 'Buzz!';
    return isBuzzEnabled ? 'Buzz!' : 'Waiting for selection';
  };

  const buzzButtonClass = state.currentQuestion?.buzzOpen && !state.currentQuestion?.buzzerId && state.currentQuestion?.status !== 'hints-exhausted'
    ? 'buzz-button buzz-button-active'
    : 'buzz-button buzz-button-locked';

  return (
    <div className="guessing-player-shell">
      <AudioPlayer audioSrc={gameshowTheme} isPlaying={isRoundOneMusic} isMuted={isMusicMuted} />
      <AudioPlayer audioSrc={guessAgainTheme} isPlaying={isGuessAgainMusic} isMuted={isMusicMuted} />
      <AudioPlayer audioSrc={lastGuessTheme} isPlaying={isLastGuessMusic} isMuted={isMusicMuted} />
      <audio ref={clueAudioRef} preload="auto" />
      
      {state.phase === 'lobby' && (
        <div className="guessing-player-lobby">
          <h2 style={{ marginTop: 0 }}>Waiting for game to start...</h2>
          <div className="guessing-player-header">
            <div className="guessing-player-info-card">
              <div className="guessing-player-label">You</div>
              <div className="guessing-player-your-name">{playerName || 'Player'}</div>
              <div className="guessing-player-your-score">${me?.points || 0}</div>
            </div>
          </div>
          <h3>Other Players</h3>
          <div className="guessing-player-list-lobby">
            {state.scoreboard?.filter((entry) => entry.name !== playerName).map((entry) => (
              <div key={entry.id} className="guessing-player-lobby-item">
                <span className="guessing-player-lobby-name">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {state.phase !== 'lobby' && me && iAmHost && (
        <div className="guessing-player-header-container">
          <div className="guessing-player-tile-header">
            <span className="guessing-player-name">{playerName || 'Player'}</span>
            <strong className="guessing-player-points">${me.points || 0}</strong>
          </div>
        </div>
      )}

      {state.phase !== 'lobby' && me && !iAmHost && (
        <div className="guessing-player-header-container">
          <div className="guessing-player-tile-header">
            <span className="guessing-player-name">{playerName || 'Player'}</span>
            <strong className="guessing-player-points">${me.points || 0}</strong>
          </div>
        </div>
      )}
      
      {state.phase !== 'lobby' && (
        <button
          className="guessing-leaderboard-toggle"
          onClick={() => setShowLeaderboard(true)}
          type="button"
        >
          Scores
        </button>
      )}

      {showLeaderboard && (
        <div className="guessing-leaderboard-overlay" onClick={() => setShowLeaderboard(false)}>
          <div className="guessing-leaderboard-modal" onClick={(event) => event.stopPropagation()}>
            <button
              className="guessing-leaderboard-close"
              onClick={() => setShowLeaderboard(false)}
              type="button"
            >
              X
            </button>
            <h3>Leaderboard</h3>
            <div className="glass-card compact">
              {leaderboardRowsFiltered.map((entry) => (
                <div className="score-item" key={`lb-${entry.id}`}>
                  <span>{entry.name}</span>
                  <strong>${entry.points}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="guessing-panel">
        {state.phase === 'host-vote' && (
          <div className="glass-card guessing-vote">
            <h3>Vote for a host</h3>
            {hostVoteSubmitted ? (
              <p>Thanks for voting.</p>
            ) : (
              <>
                <select
                  className="server-select guessing-vote-select"
                  value={selectedCandidate}
                  onChange={(e) => setSelectedCandidate(e.target.value)}
                >
                  <option value="">Select player</option>
                  {state.scoreboard?.map((entry) => (
                    <option key={entry.id} value={entry.id}>{entry.name}</option>
                  ))}
                </select>
                <button className="primary-button" onClick={voteForHost}>Submit Vote</button>
              </>
            )}
          </div>
        )}

        {state.mode === 'spiral' && state.spiral?.phase === 'initial-spin' && (
          <div className="glass-card compact">
            <h3>Initial spin</h3>
            <p>Choose 0-3. Highest spin starts and uses that movement.</p>
            <div className="answer-stack">
              {[0, 1, 2, 3].map((value) => (
                <button key={value} onClick={() => socket.emit('guessingSpiralSpinInitial', { sessionId, spin: value })}>
                  Spin {value}
                </button>
              ))}
            </div>
          </div>
        )}

        {canSelectBoardQuestion && (
          <div className="glass-card compact">
            <h3>{hostModeEnabled ? 'Host Controls' : 'Select a Question'}</h3>
            <div className="guessing-phone-board">
              {state.board?.map((category) => (
                <div key={category.id} className="guessing-phone-category">
                  <h4>{category.name}</h4>
                  {category.cells.map((cell) => (
                    <button
                      key={cell.id}
                      disabled={cell.used}
                      onClick={() => selectQuestion(cell.id)}
                      type="button"
                    >
                      {cell.used ? 'Used' : `$${cell.value}`}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {state.phase === 'question' && !iAmHost && (
          <div className="guessing-buzzer-stage">
            <button
              className={buzzButtonClass}
              disabled={!state.currentQuestion?.buzzOpen || Boolean(state.currentQuestion?.buzzerId) || state.currentQuestion?.status === 'hints-exhausted'}
              onClick={buzz}
              type="button"
            >
              <span>{buzzStatusLabel()}</span>
            </button>
            {state.settings?.hintsEnabled && iAmBuzzer && (
              <button onClick={requestHint}>Request Hint</button>
            )}
            {!state.settings?.hostSelectionEnabled && choices.length > 0 && (
              <div className="answer-stack">
                {choices.map((choice) => (
                  <button key={choice} onClick={() => submitChoice(choice)} type="button">{choice}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {state.phase === 'question' && iAmHost && state.mode === 'gameshow' && (
          <div className="glass-card compact">
            <h3>Host Controls</h3>
            {!!state.currentQuestion?.buzzerId && (
              <p><strong>Buzzed:</strong> {state.scoreboard?.find((entry) => entry.id === state.currentQuestion.buzzerId)?.name || 'Unknown'}</p>
            )}
            {/* {state.currentQuestion?.media?.imageVisible && state.currentQuestion?.image && (
              <img src={state.currentQuestion.image} alt="Clue visual" className="guessing-phone-clue-image" />
            )} */}
            {!!hostAnswer.answer && (
              <p><strong>Answer:</strong> {hostAnswer.answer}</p>
            )}
            <div className="answer-stack">
              {state.currentQuestion?.image && (
                <button type="button" onClick={handleMediaToggle}>
                  {isImageVisible ? 'Hide Image' : 'Show Image'}
                </button>
              )}
              {state.currentQuestion?.audio && (
                <>
                  <button type="button" onClick={() => mediaControl('play-audio')}>Play Audio</button>
                  <button type="button" onClick={() => mediaControl('pause-audio')}>Pause Audio</button>
                  <button type="button" onClick={() => mediaControl('stop-audio')}>Stop Audio</button>
                  <button type="button" onClick={() => mediaControl('restart-audio')}>Restart Audio</button>
                </>
              )}
            </div>
            {!isQuestionLocked && !state.currentQuestion?.buzzerId && !buzzersOpen && (
              <button className="primary-button" onClick={openBuzz} type="button">
                Open Buzzers
              </button>
            )}
            {!isQuestionLocked && !state.currentQuestion?.buzzerId && buzzersOpen && (
              <button className="danger-button" onClick={closeBuzzers} type="button">
                Close Buzzers
              </button>
            )}
            {isQuestionLocked && (
              <div className="answer-stack">
                <button className="primary-button" onClick={returnToBoard} type="button">
                  Return to Board
                </button>
              </div>
            )}
            {state.currentQuestion?.buzzerId && (
              <div className="answer-stack">
                <button className="primary-button" onClick={() => awardScore(true)} type="button">Correct</button>
                <button className="danger-button" onClick={() => awardScore(false)} type="button">Incorrect</button>
              </div>
            )}
          </div>
        )}

        {state.mode === 'spiral' && state.currentQuestion && (
          <div className="glass-card compact">
            <p>{iAmSpiralTurn ? 'It is your turn.' : 'Waiting for active player.'}</p>
            {state.settings?.hintsEnabled && iAmSpiralTurn && (
              <button onClick={requestHint}>Request Hint</button>
            )}
            {choices.length > 0 && iAmSpiralTurn && (
              <div className="answer-stack">
                {choices.map((choice) => (
                  <button key={choice} onClick={() => submitChoice(choice)}>{choice}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {state.phase === 'board' && (
          <div className="glass-card compact">
            <p>{iAmSelector ? 'You can pick the next question.' : 'Waiting for selector.'}</p>
          </div>
        )}

        {state.mode === 'spiral' && (
          <div className="glass-card compact">
            <h4>Positions</h4>
            {state.scoreboard?.map((entry) => (
              <div className="score-item" key={`pos-${entry.id}`}>
                <span>{entry.name}</span>
                <span>#{state.spiral?.positions?.[entry.id] ?? '-'}</span>
              </div>
            ))}
          </div>
        )}

        {state.phase === 'final-guess' && (
          <div className="glass-card compact">
            <h3>Last Guess</h3>
            <p>{state.finalGuess?.prompt?.question}</p>
            {finalGuessLocked ? (
              <p>Answer locked in.</p>
            ) : state.finalGuess?.prompt?.choices?.length > 0 && !iAmHost ? (
              <div className="answer-stack">
                {state.finalGuess.prompt.choices.map((choice) => (
                  <button key={choice} onClick={() => setFinalAnswer(choice)}>{choice}</button>
                ))}
              </div>
            ) : null}
            {!iAmHost && !finalGuessLocked && (
              <>
                <input type="number" value={wager} onChange={(e) => setWager(e.target.value)} placeholder="Wager" />
                <input type="text" value={finalAnswer} onChange={(e) => setFinalAnswer(e.target.value)} placeholder="Final answer" />
                <button className="primary-button" onClick={submitFinal}>Lock In</button>
              </>
            )}
          </div>
        )}

        {state.phase === 'final-guess' && iAmHost && (
          <div className="glass-card compact">
            <h3>Host Final Judging</h3>
            <p>{hostAnswer.answer || state.finalGuess?.reviewCurrent?.answer || 'Waiting for answers...'}</p>
            {state.finalGuess?.reviewCurrent?.scored ? (
              <p>Answer locked in.</p>
            ) : state.finalGuess?.reviewCurrent ? (
              <>
                <p><strong>{state.finalGuess.reviewCurrent.name}</strong></p>
                <p>Wager: ${state.finalGuess.reviewCurrent.wager}</p>
                <div className="answer-stack">
                  <button onClick={() => scoreFinal(state.finalGuess.reviewCurrent.playerId, true)} type="button">Correct</button>
                  <button onClick={() => scoreFinal(state.finalGuess.reviewCurrent.playerId, false)} type="button">Incorrect</button>
                </div>
              </>
            ) : (
              <p>Waiting for everyone to lock in an answer.</p>
            )}
          </div>
        )}

        {state.phase === 'ended' && (
          <div className="glass-card compact">
            <h3>Final Leaderboard</h3>
            {leaderboardRowsFiltered.map((entry) => (
              <div className="score-item" key={`final-${entry.id}`}>
                <span>{entry.name}</span>
                <strong>${entry.points}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerScreen;
